import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import styles from './SpellingModes.module.css';
import AITutor from '../AITutor/AITutor';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaVolumeUp, FaPlay, FaPause } from 'react-icons/fa';
import { API_BASE_URL, apiRequest } from '../../config/api';
import Loader from '../common/Loader/Loader';

const SPELLING_QUESTION_TYPES = {
  WORD_COMPLETION: 'word_completion',
  AUDIO_SPELLING: 'audio_spelling',
  TYPING_CHALLENGE: 'typing_challenge',
  ANAGRAM_CHALLENGE: 'anagram_challenge',
  DEFINITION_SPELLING: 'definition_spelling',
  SPELLING_BEE: 'spelling_bee',
  STORY_SPELLING: 'story_spelling'
};

const SpellingBeeMode = () => {
  const { userProgress, loading: userLoading, error: userError, updateProgress } = useUser();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const initialized = useRef(false);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(30);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const timerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [scrambledLetters, setScrambledLetters] = useState([]);
  const [completingSession, setCompletingSession] = useState(false);
  
  // Speech synthesis setup
  const synth = window.speechSynthesis;
  const [selectedVoice, setSelectedVoice] = useState(null);

  const navigate = useNavigate();
  const { state } = useLocation();
  const difficulty = state?.difficulty || userProgress?.modeLevels?.spelling_bee || 1;

  // Initialize voice when component mounts
  useEffect(() => {
    const initVoice = () => {
      const voices = synth.getVoices();
      // Prioritize British English voices
      const britishVoice = voices.find(voice => voice.lang === 'en-GB');
      const englishVoice = voices.find(voice => voice.lang.startsWith('en-'));
      
      if (britishVoice) {
        setSelectedVoice(britishVoice);
      } else if (englishVoice) {
        setSelectedVoice(englishVoice);
      }
    };

    if (synth.onvoiceschanged !== undefined) {
      synth.onvoiceschanged = initVoice;
    }
    initVoice();
  }, []);

  useEffect(() => {
    if (!userLoading && userProgress && !initialized.current) {
      startPractice();
      initialized.current = true;
    }
  }, [userLoading, userProgress]);

  useEffect(() => {
    if (questions.length > 0) {
      initializeQuestion();
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [currentQuestion, questions]);

  const initializeQuestion = () => {
    setQuestionStartTime(Date.now());
    setUserInput('');
    setShowExplanation(false);
    setFeedback(null);
    setTimeLeft(30); // Set time limit to 30 seconds for each question
    setIsPlaying(false);

    if (timerRef.current) clearInterval(timerRef.current);
    
    timerRef.current = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 0) {
          clearInterval(timerRef.current);
          handleTimeUp();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    // Initialize based on question type
    const currentQ = questions[currentQuestion];
    if (currentQ) {
      if (currentQ.type === SPELLING_QUESTION_TYPES.ANAGRAM_CHALLENGE) {
        // Use the scrambled version from the backend
        if (currentQ.content.scrambledVersion) {
          setScrambledLetters(currentQ.content.scrambledVersion.split(''));
        }
      } else if (currentQ.type === SPELLING_QUESTION_TYPES.AUDIO_SPELLING) {
        // Automatically play the word for audio questions
        setTimeout(() => playWord(), 500);
      }
    }
  };

  const playWord = () => {
    if (questions[currentQuestion] && 
        (questions[currentQuestion].type === SPELLING_QUESTION_TYPES.AUDIO_SPELLING ||
         questions[currentQuestion].type === SPELLING_QUESTION_TYPES.SPELLING_BEE)) {
      
      if (isPlaying) {
        synth.cancel();
        setIsPlaying(false);
        return;
      }

      const utterance = new SpeechSynthesisUtterance(questions[currentQuestion].content.correctAnswer);
      
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
      
      utterance.rate = 0.6; // Slower for better clarity
      utterance.pitch = 1;
      
      utterance.onend = () => {
        setIsPlaying(false);
      };

      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event);
        setIsPlaying(false);
        setError('Failed to play audio. Please try again.');
      };

      setIsPlaying(true);
      synth.speak(utterance);
    }
  };

  const handleTimeUp = async () => {
    try {
      const sessionId = localStorage.getItem('currentSessionId');
      const currentQ = questions[currentQuestion];
      const timeSpent = (Date.now() - questionStartTime) / 1000;
      
      await apiRequest(`/sessions/${sessionId}/answer`, {
        method: 'POST',
        body: JSON.stringify({
          questionId: currentQ._id,
          answer: userInput.trim() || '',
          timeSpent,
          timedOut: true,
          streak
        })
      });

      setStreak(0); // Reset streak on time up
      setFeedback({
        isCorrect: false,
        message: "Time's up! The correct word was: " + currentQ.content.correctAnswer
      });
      setShowExplanation(true);

    } catch (err) {
      setError(err.message);
    }
  };

  const startPractice = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        mode: 'spelling_bee',
        difficulty: difficulty
      });

      const questionsData = await apiRequest(`/spelling/questions?${params}`);

      const sessionData = await apiRequest('/sessions/start', {
        method: 'POST',
        body: JSON.stringify({
          mode: 'spelling_bee',
          difficulty: difficulty,
          questions: questionsData.map(q => q._id)
        })
      });

      setQuestions(questionsData);
      localStorage.setItem('currentSessionId', sessionData._id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !userInput.trim()) return;
    setIsSubmitting(true);

    try {
      const sessionId = localStorage.getItem('currentSessionId');
      const currentQ = questions[currentQuestion];
      const timeSpent = (Date.now() - questionStartTime) / 1000;
      const isCorrect = userInput.trim().toLowerCase() === currentQ.content.correctAnswer.toLowerCase();
      
      // Update streak
      const newStreak = isCorrect ? streak + 1 : 0;
      setStreak(newStreak);
      setBestStreak(Math.max(bestStreak, newStreak));

      const result = await apiRequest(`/sessions/${sessionId}/answer`, {
        method: 'POST',
        body: JSON.stringify({
          questionId: currentQ._id,
          answer: userInput.trim(),
          timeSpent,
          streak: newStreak
        })
      });

      setFeedback({
        isCorrect: result.isCorrect,
        message: result.isCorrect ? 
          'Correct!' : 
          `Incorrect. The word was: ${currentQ.content.correctAnswer}`
      });
      setShowExplanation(true);
      if (timerRef.current) clearInterval(timerRef.current);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    setShowExplanation(false);
    
    if (currentQuestion + 1 >= questions.length) {
      try {
        setCompletingSession(true);
        const sessionId = localStorage.getItem('currentSessionId');
        
        const results = await apiRequest(`/sessions/${sessionId}/end`, {
          method: 'POST',
          body: JSON.stringify({
            bestStreak
          })
        });

        localStorage.removeItem('currentSessionId');
        updateProgress(results);
        
        // Navigate to results page with session results and questions
        navigate('/results', { 
          state: { 
            sessionResults: results,
            questions: questions
          } 
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setCompletingSession(false);
      }
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  if (loading || userLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  // Render different question UI based on question type
  const renderQuestionContent = () => {
    if (!currentQ) return null;

    switch(currentQ.type) {
      case SPELLING_QUESTION_TYPES.WORD_COMPLETION:
        return (
          <div className={styles.questionContent}>
            <h1 style={{marginBottom: '15px'}}><strong>Rewrite the word by filling in the missing letter(s)</strong></h1>
            <p className={styles.questionText}>{currentQ.content.text}</p>
            {currentQ.content.hints?.map((hint, index) => (
              <div key={index} className={styles.hint}>{hint}</div>
            ))}
          </div>
        );
      
      case SPELLING_QUESTION_TYPES.AUDIO_SPELLING:
        return (
          <div className={styles.audioSection}>
            <button 
              onClick={playWord}
              className={styles.audioButton}
              disabled={showExplanation}
            >
              {isPlaying ? <FaPause /> : <FaVolumeUp />}
              {isPlaying ? 'Stop' : 'Play Word'}
            </button>
            <p className={styles.instruction}>Listen carefully and spell the word you hear</p>
          </div>
        );
      
      case SPELLING_QUESTION_TYPES.DEFINITION_SPELLING:
        return (
          <div className={styles.definitionContainer}>
            <h2><strong>Spell the word</strong></h2>
            <div className={`${styles.definition} ${styles.definitionSpelling}`}>
              <h3>Definition:</h3>
              <p>{currentQ.content.definition}</p>
            </div>
            <div className={styles.hintContainer}>
              <p><strong>Hint:</strong> {currentQ.content.hints[0]}</p>
            </div>
            <p className={styles.instruction}>Type the word that matches this definition</p>
          </div>
        );
      
      case SPELLING_QUESTION_TYPES.ANAGRAM_CHALLENGE:
        return (
          <div className={styles.anagramContainer}>
            <h1 style={{marginBottom: '15px'}}><strong>Unscramble the letters to make the right word!</strong></h1>
            <div className={styles.scrambledLetters}>
              {scrambledLetters.map((letter, index) => (
                <span key={index} className={styles.letter}>
                  {letter}
                </span>
              ))}
            </div>
            {currentQ.content.hints?.map((hint, index) => (
              <div key={index} className={styles.hint}>{hint}</div>
            ))}
          </div>
        );
      
      default:
        return (
          <div className={styles.questionContent}>
            <p>{currentQ.content.text || "Spell the word correctly"}</p>
          </div>
        );
    }
  };

  return (
    <div className={styles.container}>
      {completingSession && (
        <div className={styles.overlay}>
          <Loader size="large" text="Analyzing your results..." />
        </div>
      )}
      <div className={styles.card}>
        <AITutor 
          currentQuestion={currentQ}
          userAnswer={userInput}
          isCorrect={feedback?.isCorrect}
        />
        
        <div className={styles.header}>
          <h2 className={styles.title}>Spelling Bee</h2>
          <span className={styles.questionCount}>
            Question {currentQuestion + 1} of {questions.length}
          </span>
        </div>

        <div className={styles.progressContainer}>
          <div className={styles.progressBar} style={{ width: `${progress}%` }} />
        </div>

        <div className={styles.statsContainer}>
          <div className={styles.stat}>
            <span>Time Left: {timeLeft}s</span>
          </div>
          <div className={styles.stat}>
            <span>Streak: {streak}</span>
          </div>
          <div className={styles.stat}>
            <span>Best Streak: {bestStreak}</span>
          </div>
        </div>

        {renderQuestionContent()}

        <form onSubmit={handleSubmit} className={styles.inputForm}>
          <input
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            className={styles.answerInput}
            placeholder="Type your answer..."
            disabled={showExplanation}
            autoFocus
          />
          {!showExplanation && (
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={isSubmitting || !userInput.trim()}
            >
              Submit
            </button>
          )}
        </form>

        {feedback && (
          <div className={`${styles.feedback} ${feedback.isCorrect ? styles.correct : styles.incorrect}`}>
            {feedback.message}
          </div>
        )}

        {showExplanation && (
          <>
            <div className={styles.explanation}>
              <p>Correct answer: {currentQ.content.correctAnswer}</p>
              <p>Definition: {currentQ.content.definition}</p>
              <p>Usage: {currentQ.content.usageExample}</p>
              {currentQ.content.pronunciation && (
                <p>Pronunciation: {currentQ.content.pronunciation}</p>
              )}
              {currentQ.content.wordFamily && (
                <div className={styles.wordFamily}>
                  <h3>Word Family:</h3>
                  <p>{currentQ.content.wordFamily}</p>
                </div>
              )}
            </div>
            <button 
              className={styles.nextButton}
              onClick={handleNext}
            >
              {currentQuestion + 1 >= questions.length ? 'Finish' : 'Next Word'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SpellingBeeMode; 